/**
 * Mulligan window TanStack Query hooks (host-only CRUD).
 *
 * Wraps the apiClient methods exposed by `services/api/mulligan.ts`.
 * Mutations invalidate `mulliganWindowKeys.list(poolId)` so the window list
 * stays in lock-step with the backend after create/update/delete.
 *
 * Requirements: 1.1, 1.13, 8.1, 10.1
 * Design: Frontend Components → TanStack Query Hooks
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  CreateMulliganWindowInput,
  UpdateMulliganWindowInput,
} from '@/services/api';
import { MulliganApiError } from '@/services/api/mulligan.errors';
import { mulliganStatusKeys } from './useMulliganStatus';

/**
 * Query key factory for mulligan windows.
 */
export const mulliganWindowKeys = {
  all: ['mulligan-windows'] as const,
  lists: () => [...mulliganWindowKeys.all, 'list'] as const,
  list: (poolId: string) => [...mulliganWindowKeys.lists(), poolId] as const,
} as const;

/**
 * Hook to list all mulligan windows for a pool.
 *
 * Members get the windows annotated with `isOpen` so the UI can highlight
 * which window is currently active.
 */
export function useMulliganWindows(poolId: string) {
  return useQuery({
    queryKey: mulliganWindowKeys.list(poolId),
    queryFn: async () => {
      const response = await api.mulligan.listMulliganWindows(poolId);
      if (response.error) {
        throw new MulliganApiError(response.error);
      }
      return response.data;
    },
    enabled: !!poolId,
  });
}

/**
 * Hook to create a new mulligan window. Host-only on the backend.
 */
export function useCreateMulliganWindow(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateMulliganWindowInput) => {
      const response = await api.mulligan.createMulliganWindow(poolId, input);
      if (response.error) {
        throw new MulliganApiError(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mulliganWindowKeys.list(poolId),
      });
      // Status panel reflects the new window's state and allowance.
      queryClient.invalidateQueries({
        queryKey: mulliganStatusKeys.pool(poolId),
      });
    },
  });
}

/**
 * Hook to update an existing mulligan window. Host-only on the backend.
 */
export function useUpdateMulliganWindow(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      windowId: string;
      patch: UpdateMulliganWindowInput;
    }) => {
      const response = await api.mulligan.updateMulliganWindow(
        poolId,
        variables.windowId,
        variables.patch
      );
      if (response.error) {
        throw new MulliganApiError(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mulliganWindowKeys.list(poolId),
      });
      queryClient.invalidateQueries({
        queryKey: mulliganStatusKeys.pool(poolId),
      });
    },
  });
}

/**
 * Hook to delete a mulligan window. Host-only on the backend.
 *
 * The backend rejects with `WINDOW_HAS_SWAPS` when any swap has been
 * applied against the window; we surface that as a thrown Error.
 */
export function useDeleteMulliganWindow(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (windowId: string) => {
      const response = await api.mulligan.deleteMulliganWindow(
        poolId,
        windowId
      );
      if (response.error) {
        throw new MulliganApiError(response.error);
      }
      return { windowId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mulliganWindowKeys.list(poolId),
      });
      queryClient.invalidateQueries({
        queryKey: mulliganStatusKeys.pool(poolId),
      });
    },
  });
}
